import {differenceInCalendarDays, parseISO} from 'date-fns'
import {z} from 'zod'

import {tzDateFromIso} from 'src/api/zod-helpers'
import type {UnconfirmedRental} from 'src/rentals/confirm-out-panel'

import {identifiedRentalAssetSchema} from '../assets/rental-assets/schemas'
import {workerSchema} from '../assets/workers/schemas'
import {toBusinessApiDateString} from '../jobs/job-schedule-format'
import {
    mapPartialPickup,
    type PartialPickup,
    partialPickupRawSchema,
} from '../work/partial-pickup-schema'

export const dispatchAttentionCountSchema = z.object({
    count: z.number().int().nonnegative(),
})

export type DispatchAttentionCount = z.infer<typeof dispatchAttentionCountSchema>

const dispatchBoardLocationRawSchema = z.object({
    id: z.string(),
    name: z.string(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    notes: z.string().nullable(),
    full_address: z.string().nullable().optional(),
})

const dispatchBoardWorkerRequirementRawSchema = z.object({
    id: z.string(),
    worker_type_id: z.string(),
    worker_type_name: z.string(),
    is_driver: z.boolean(),
    quantity: z.number().int(),
    assigned_quantity: z.number().int(),
})

const dispatchBoardAssetRequirementRawSchema = z.object({
    id: z.string(),
    rental_asset_type_id: z.string(),
    rental_asset_type_name: z.string(),
    quantity: z.number().int(),
    assigned_quantity: z.number().int(),
    on_site_quantity: z.number().int().optional(),
})

const dispatchBoardWorkerAssignmentRawSchema = z.object({
    id: z.string(),
    worker_id: z.string(),
    worker_name: z.string(),
    worker_type_id: z.string(),
    is_placeholder: z.boolean(),
    notified_at: z.string().nullable(),
    start_date: z.string().optional(),
    end_date: z.string().nullable().optional(),
})

const dispatchBoardUniqueAssetAssignmentRawSchema = z.object({
    id: z.string(),
    rental_asset_id: z.string(),
    rental_asset_name: z.string(),
    rental_asset_type_id: z.string(),
    notes: z.string().nullable(),
})

const dispatchBoardStockAssetAssignmentRawSchema = z.object({
    rental_asset_type_id: z.string(),
    rental_asset_type_name: z.string(),
    quantity: z.number().int(),
})

const dispatchBoardJobRawSchema = z.object({
    id: z.string(),
    display_id: z.string(),
    name: z.string(),
    status: z.enum(['planned', 'active', 'completed', 'cancelled']),
    description: z.string().nullable(),
    service_type_name: z.string(),
    project_id: z.string(),
    project_name: z.string(),
    internal_contact_name: z.string().nullable().optional(),
    rep_id: z.string().nullable(),
    project_created_by_id: z.string().nullable(),
    created_by_id: z.string().nullable(),
    customer_id: z.string(),
    customer_name: z.string(),
    dispatch_yard_id: z.string().nullable(),
    dispatch_yard_name: z.string().nullable(),
    closer_yard_id: z.string().nullable(),
    closer_yard_name: z.string().nullable(),
    closer_yard_reason: z.string().nullable(),
    book_by_day: z.boolean(),
    is_pickup: z.boolean(),
    partial_pickup: partialPickupRawSchema.nullable(),
    did_not_happen_at: z.string().nullable(),
    pickup_confirmed_at: z.string().nullable(),
    unconfirmed_rentals: z
        .array(z.object({rental_stint_id: z.string(), unit: z.string(), since: z.string()}))
        .optional(),
    start_date: z.string(),
    start_time: z.string().nullable(),
    end_date: z.string().nullable(),
    end_time: z.string().nullable(),
    date_tbc: z.boolean(),
    locations: z.array(dispatchBoardLocationRawSchema),
    worker_requirements: z.array(dispatchBoardWorkerRequirementRawSchema),
    asset_requirements: z.array(dispatchBoardAssetRequirementRawSchema),
    driver_owned_truck_types: z.array(z.string()),
    on_site_equipment_types: z.array(z.string()),
    worker_assignments: z.array(dispatchBoardWorkerAssignmentRawSchema),
    asset_assignments: z.array(
        z.union([
            dispatchBoardUniqueAssetAssignmentRawSchema,
            dispatchBoardStockAssetAssignmentRawSchema,
        ]),
    ),
    allocation_status: z.enum(['needs_resources', 'partially_allocated', 'ready']),
})

export const dispatchBoardResponseRawSchema = z.object({
    start_date: z.string(),
    end_date: z.string(),
    workers: z.array(workerSchema),
    rental_assets: z.array(identifiedRentalAssetSchema),
    jobs: z.array(dispatchBoardJobRawSchema),
    unavailable_drivers: z
        .array(
            z.object({
                worker_id: z.string(),
                start_date: z.string(),
                end_date: z.string(),
                label: z.string(),
                detail: z.string(),
            }),
        )
        .optional(),
    out_of_service_equipment: z
        .array(
            z.object({
                rental_asset_id: z.string(),
                label: z.string(),
                note: z.string().nullable(),
                expected_back: z.string().nullable(),
            }),
        )
        .optional(),
    units_on_rent: z
        .array(
            z.object({
                rental_asset_id: z.string(),
                project_id: z.string(),
                order: z.string(),
                since: z.string(),
            }),
        )
        .optional(),
})

export type DispatchBoardResponseRaw = z.infer<typeof dispatchBoardResponseRawSchema>

export type DispatchBoardJobRaw = z.infer<typeof dispatchBoardJobRawSchema>

export type DispatchBoardResponse = {
    startDate: string
    endDate: string
    workers: z.infer<typeof workerSchema>[]
    rentalAssets: z.infer<typeof identifiedRentalAssetSchema>[]
    jobs: DispatchBoardJob[]
    unavailableDrivers: Array<{
        workerId: string
        from: string
        until: string
        label: string
        detail: string
    }>
    outOfServiceEquipment: Array<{
        rentalAssetId: string
        label: string
        note: string | null
        expectedBack: string | null
    }>
    unitsOnRent?: Array<{rentalAssetId: string; projectId: string; order: string; since: string}>
}

export type DispatchBoardJob = {
    id: string
    displayId: string
    name: string
    status: 'planned' | 'active' | 'completed' | 'cancelled'
    description: string | null
    serviceTypeName: string
    projectId: string
    projectName: string
    internalContactName: string | null
    repId: string | null
    projectCreatedById: string | null
    createdById: string | null
    customerId: string
    customerName: string
    dispatchYardId: string | null
    dispatchYardName: string | null
    closerYardId: string | null
    closerYardName: string | null
    closerYardReason: string | null
    /** Worker assignments are per day. */
    bookByDay: boolean
    isPickup: boolean
    partialPickup: PartialPickup | null
    didNotHappenAt: string | null
    pickupConfirmedAt: string | null
    /** Equipment put on rent automatically that dispatch has not confirmed went out. */
    unconfirmedRentals: UnconfirmedRental[]
    startDate: string
    startTime: string | null
    endDate: string | null
    endTime: string | null
    /** Start and end dates are mapped to today while true. */
    dateTbc: boolean
    /** Job length in days, or null when there is no end date. */
    dateTbcDayCount: number | null
    locations: Array<{
        id: string
        name: string
        latitude: number | null
        longitude: number | null
        notes: string | null
        addressText?: string | null
    }>
    workerRequirements: Array<{
        id: string
        workerTypeId: string
        workerTypeName: string
        isDriver: boolean
        quantity: number
        assignedQuantity: number
    }>
    assetRequirements: Array<{
        id: string
        rentalAssetTypeId: string
        rentalAssetTypeName: string
        quantity: number
        assignedQuantity: number
        onSiteQuantity?: number
    }>
    driverOwnedTruckTypes: string[]
    onSiteEquipmentTypes: string[]
    workerAssignments: Array<{
        id: string
        workerId: string
        workerName: string
        workerTypeId: string
        isPlaceholder: boolean
        notifiedAt: number | null
        startDate?: string
        endDate?: string | null
    }>
    assetAssignments: Array<
        | {
              kind: 'unique'
              id: string
              rentalAssetId: string
              rentalAssetName: string
              rentalAssetTypeId: string
              notes: string | null
          }
        | {
              kind: 'stock'
              rentalAssetTypeId: string
              rentalAssetTypeName: string
              quantity: number
          }
    >
    allocationStatus: 'needs_resources' | 'partially_allocated' | 'ready'
}

function mapBoardJob(raw: DispatchBoardJobRaw): DispatchBoardJob {
    const today = toBusinessApiDateString(new Date())
    const dateTbc = raw.date_tbc
    return {
        id: raw.id,
        displayId: raw.display_id,
        name: dateTbc ? `${raw.name} · date TBC` : raw.name,
        status: raw.status,
        description: raw.description,
        serviceTypeName: raw.service_type_name,
        projectId: raw.project_id,
        projectName: raw.project_name,
        internalContactName: raw.internal_contact_name ?? null,
        repId: raw.rep_id,
        projectCreatedById: raw.project_created_by_id,
        createdById: raw.created_by_id,
        customerId: raw.customer_id,
        customerName: raw.customer_name,
        dispatchYardId: raw.dispatch_yard_id,
        dispatchYardName: raw.dispatch_yard_name,
        closerYardId: raw.closer_yard_id,
        closerYardName: raw.closer_yard_name,
        closerYardReason: raw.closer_yard_reason,
        bookByDay: raw.book_by_day,
        isPickup: raw.is_pickup,
        partialPickup: raw.partial_pickup ? mapPartialPickup(raw.partial_pickup) : null,
        didNotHappenAt: raw.did_not_happen_at,
        pickupConfirmedAt: raw.pickup_confirmed_at,
        unconfirmedRentals: (raw.unconfirmed_rentals ?? []).map((rental) => ({
            rentalStintId: rental.rental_stint_id,
            unit: rental.unit,
            since: rental.since,
        })),
        startDate: dateTbc ? today : raw.start_date,
        startTime: raw.start_time,
        endDate: dateTbc ? today : raw.end_date,
        endTime: raw.end_time,
        dateTbc,
        dateTbcDayCount:
            dateTbc && raw.end_date != null
                ? differenceInCalendarDays(parseISO(raw.end_date), parseISO(raw.start_date)) + 1
                : null,
        locations: raw.locations.map((location) => ({
            id: location.id,
            name: location.name,
            latitude: location.latitude,
            longitude: location.longitude,
            notes: location.notes,
            addressText: location.full_address ?? null,
        })),
        workerRequirements: raw.worker_requirements.map((requirement) => ({
            id: requirement.id,
            workerTypeId: requirement.worker_type_id,
            workerTypeName: requirement.worker_type_name,
            isDriver: requirement.is_driver,
            quantity: requirement.quantity,
            assignedQuantity: requirement.assigned_quantity,
        })),
        assetRequirements: raw.asset_requirements.map((requirement) => ({
            id: requirement.id,
            rentalAssetTypeId: requirement.rental_asset_type_id,
            rentalAssetTypeName: requirement.rental_asset_type_name,
            quantity: requirement.quantity,
            assignedQuantity: requirement.assigned_quantity,
            onSiteQuantity: requirement.on_site_quantity ?? 0,
        })),
        driverOwnedTruckTypes: raw.driver_owned_truck_types,
        onSiteEquipmentTypes: raw.on_site_equipment_types,
        workerAssignments: raw.worker_assignments.map((assignment) => ({
            id: assignment.id,
            workerId: assignment.worker_id,
            workerName: assignment.worker_name,
            workerTypeId: assignment.worker_type_id,
            isPlaceholder: assignment.is_placeholder,
            notifiedAt:
                assignment.notified_at == null
                    ? null
                    : tzDateFromIso.parse(assignment.notified_at).getTime(),
            startDate: assignment.start_date,
            endDate: assignment.end_date ?? null,
        })),
        assetAssignments: raw.asset_assignments.map((assignment) => {
            if ('rental_asset_id' in assignment) {
                return {
                    kind: 'unique' as const,
                    id: assignment.id,
                    rentalAssetId: assignment.rental_asset_id,
                    rentalAssetName: assignment.rental_asset_name,
                    rentalAssetTypeId: assignment.rental_asset_type_id,
                    notes: assignment.notes,
                }
            }

            return {
                kind: 'stock' as const,
                rentalAssetTypeId: assignment.rental_asset_type_id,
                rentalAssetTypeName: assignment.rental_asset_type_name,
                quantity: assignment.quantity,
            }
        }),
        allocationStatus: raw.allocation_status,
    }
}

export const dispatchBoardResponseSchema = dispatchBoardResponseRawSchema.transform((raw) => ({
    startDate: raw.start_date,
    endDate: raw.end_date,
    workers: raw.workers,
    rentalAssets: raw.rental_assets,
    jobs: raw.jobs.map(mapBoardJob),
    unavailableDrivers: (raw.unavailable_drivers ?? []).map((row) => ({
        workerId: row.worker_id,
        from: row.start_date,
        until: row.end_date,
        label: row.label,
        detail: row.detail,
    })),
    outOfServiceEquipment: (raw.out_of_service_equipment ?? []).map((row) => ({
        rentalAssetId: row.rental_asset_id,
        label: row.label,
        note: row.note,
        expectedBack: row.expected_back,
    })),
    unitsOnRent: (raw.units_on_rent ?? []).map((row) => ({
        rentalAssetId: row.rental_asset_id,
        projectId: row.project_id,
        order: row.order,
        since: row.since,
    })),
}))

export const dispatchBoardJobResponseSchema = dispatchBoardJobRawSchema.transform(mapBoardJob)

export const dispatchYardSchema = z.object({
    id: z.string(),
    name: z.string(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    sites: z.array(z.object({name: z.string(), latitude: z.number(), longitude: z.number()})),
})

export type DispatchYardRaw = z.input<typeof dispatchYardSchema>
export type DispatchYard = z.infer<typeof dispatchYardSchema>

export const setProjectDispatchYardRequestSchema = z
    .object({dispatchYardId: z.string()})
    .transform(({dispatchYardId}) => ({dispatch_yard_id: dispatchYardId}))

export type SetProjectDispatchYardRequestRaw = z.infer<typeof setProjectDispatchYardRequestSchema>
